import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  Container,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import LogoutIcon from '@mui/icons-material/Logout';

import Knowledge from './components/Knowledge';
import Archives from './components/Archives';
import Prompt from './components/Prompt';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';

const menuItems = [
  { text: 'Knowledge', path: '/', icon: LightbulbOutlinedIcon },
  { text: 'Archives', path: '/archives', icon: ArchiveOutlinedIcon },
  { text: 'Prompt', path: '/prompt', icon: QuestionAnswerOutlinedIcon },
];

const AppContent = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <ListItemButton component={Link} to={item.path} key={item.text}>
              <IconButton edge="start" color="inherit" sx={{ mr: 2 }}>
                <Icon />
              </IconButton>
              <ListItemText primary={item.text} />
            </ListItemButton>
          );
        })}
        <ListItemButton onClick={logout}>
          <IconButton edge="start" color="inherit" sx={{ mr: 2 }}>
            <LogoutIcon />
          </IconButton>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ 
          background: 'linear-gradient(90deg, rgba(17, 17, 17, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.1)'
        }}>
          <img
            src="/mazelogo.png"
            alt="Logo"
            style={{ 
              marginRight: '10px', 
              marginTop: '10px', 
              marginBottom: '10px', 
              height: '60px',
              filter: 'brightness(1.2)'
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1,
              background: 'linear-gradient(45deg, #D4AF37 30%, #FFD700 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600,
              letterSpacing: '0.02em'
            }}
          >
            Drinks Intelligence
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button 
                  color="inherit" 
                  component={Link} 
                  to={item.path} 
                  key={item.text} 
                  startIcon={<Icon />}
                  sx={{
                    color: '#D4AF37',
                    '&:hover': {
                      color: '#FFD700',
                      background: 'rgba(212, 175, 55, 0.05)',
                    },
                    mx: 1
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
            <Button 
              color="inherit" 
              onClick={logout} 
              startIcon={<LogoutIcon />}
              sx={{
                color: '#D4AF37',
                '&:hover': {
                  color: '#FFD700',
                  background: 'rgba(212, 175, 55, 0.05)',
                },
                mx: 1
              }}
            >
              Logout
            </Button>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ 
              display: { xs: 'block', sm: 'none' },
              color: '#D4AF37',
              '&:hover': {
                color: '#FFD700',
                background: 'rgba(212, 175, 55, 0.05)',
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{ display: { xs: 'block', sm: 'none' } }}
      >
        {drawerList()}
      </Drawer>
      <Container style={{ marginTop: '2rem' }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Knowledge />
            </ProtectedRoute>
          } />
          <Route path="/archives" element={
            <ProtectedRoute>
              <Archives />
            </ProtectedRoute>
          } />
          <Route path="/prompt" element={
            <ProtectedRoute>
              <Prompt />
            </ProtectedRoute>
          } />
        </Routes>
      </Container>
    </>
  );
};

const App = () => {
  return (
    <Router>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;

